import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const CartContext = createContext(null);

const getOrCreateSessionId = () => {
  if (typeof window === 'undefined') return '';
  let sid = localStorage.getItem('sb_visitor_id');
  if (!sid) {
    sid = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'v-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('sb_visitor_id', sid);
  }
  return sid;
};

const loadInitialCart = () => {
  if (typeof window === 'undefined') return [];
  try {
    const s = localStorage.getItem('cart');
    return s ? JSON.parse(s) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(loadInitialCart);
  const [sessionId] = useState(getOrCreateSessionId);
  const debounceRef = useRef(null);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));

    // Skip tracking on the very first render (no user action yet)
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
      api.post('/cart/track', {
        session_id: sessionId,
        items: cart,
        total,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      }).catch(() => {});
    }, 800);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [cart, sessionId]);

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        product_image: product.images?.[0] || '',
        price: product.sale_price || product.price,
        quantity
      }];
    });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount, sessionId, hydrated: true }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
